import { Box } from "@mui/material"
import { useRef } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import useWheelScroll from "../../hooks/useWheelScroll"
import CategoryItem from "./CategoryItem"
import { FaAngleLeft } from "../../assets/svg/button-icons/FaAngleLeft"
import { FaAngleRight } from "../../assets/svg/button-icons/FaAngleRight"
import { categoryTab, categoryTabsCont, categoryTabsWrapper } from "./categoryTabsStyles"
import style from "./styles.module.css"
import { useCategoryProvider } from "../../common/category/useCategoryProvider"
import { selectCurrentTypeIds, selectFilter } from "../../shared/filter/selectors"
import { updateCategoryId, setShowAll, updateDateRange } from "../../shared/filter/filterSlice"

const CategoryTabs = () => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useAppDispatch()
  const { categories } = useCategoryProvider()
  const currentIds = useAppSelector(selectCurrentTypeIds)
  const showAll = useAppSelector(selectFilter).showAll

  const touchStartX = useRef<number>(0)
  const touchStartScroll = useRef<number>(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartScroll.current = containerRef.current?.scrollLeft || 0
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current) return
    const dx = touchStartX.current - e.touches[0].clientX
    containerRef.current.scrollLeft = touchStartScroll.current + dx
  }

  // Нажали на категорию
  const handleNavigateTabs = (value: number) => {
    dispatch(updateCategoryId(value))
  }

  // Есть ли выбранная конкретная категория
  const hasSelection = Array.isArray(currentIds) && currentIds.length > 0
  // «Все» включена: нет выбранных категорий и showAll не выключен кнопкой
  const allActive = showAll !== false && !hasSelection

  // «Все» — переключатель.
  // Включена → повторный клик выключает: лента скрывается до выбора категории или нового клика.
  // Выключена или есть категория → включает: сбрасывает категории И даты, показывает все события.
  const handleSelectAll = () => {
    if (allActive) {
      dispatch(setShowAll(false))
      return
    }
    dispatch(updateCategoryId([]))
    dispatch(setShowAll(true))
    dispatch(updateDateRange([undefined, undefined]))
  }

  useWheelScroll(containerRef)

  const scrollContainer = containerRef.current
  const scrollContainerWidth = scrollContainer?.getBoundingClientRect().width || 0
  const tabWidth = 108
  const scrollAmount = scrollContainerWidth - (scrollContainerWidth % tabWidth)

  const handleArrowClick = (direction: "left" | "right"): void => {
    if (scrollContainer) {
      scrollContainer.scrollLeft += direction === "left" ? -scrollAmount : scrollAmount
    }
  }

  return (
    <Box sx={categoryTabsWrapper}>
      <Box
        sx={categoryTabsCont}
        ref={containerRef}
        onTouchStart={handleTouchStart as any}
        onTouchMove={handleTouchMove as any}
      >
        {/* Кнопка "Все" */}
        <Box
          onClick={handleSelectAll}
          sx={{
            ...categoryTab,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "4px",
            flexShrink: 0,
            width: "auto",
            minWidth: "81px",
            maxWidth: "100px",
            opacity: hasSelection ? 0.45 : 1,
            transition: "opacity 0.2s ease",
            background: allActive
              ? "linear-gradient(135deg, rgba(124,106,245,0.5) 0%, rgba(168,85,247,0.5) 100%)"
              : "transparent",
            borderRadius: "16px",
            border: allActive ? "none" : "2px solid var(--borders)",
          }}
        >
          <span style={{ fontSize: "28px", lineHeight: 1 }}>🎯</span>
          <span style={{
            fontSize: "11px",
            fontWeight: 700,
            color: allActive ? "#fff" : "var(--primary-text)",
          }}>
            Все
          </span>
        </Box>

        {categories.map((cat) => (
          <CategoryItem
            key={cat.id}
            category={cat}
            click={handleNavigateTabs}
          />
        ))}

        <button
          className={style.category_arrow}
          aria-label="scroll left arrow"
          onClick={() => handleArrowClick("left")}
        >
          <FaAngleLeft style={{ marginLeft: "8px" }} />
        </button>
        <button
          className={style.category_arrow}
          aria-label="scroll right arrow"
          onClick={() => handleArrowClick("right")}
        >
          <FaAngleRight style={{ marginLeft: "16px" }} />
        </button>
      </Box>
    </Box>
  )
}

export default CategoryTabs
